import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { useEffect, useMemo, useRef, useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { DraggableWidget } from "./DraggableWidget";
import { SortableWidgets } from "./SortableWidgets";
import { CanvasDropArea } from "./CanvasDropArea";
import { WidgetLibraryCard } from "./WidgetLibraryCard";
import { nanoid } from 'nanoid';
import { PageInnerLayout } from "components/pageInnerLayout";
import { Button } from "components/ui/button";
import { useAddTemplates, useGetTemplateById, useUpdateTemplateData } from "hooks/templates";
import { useNotificationBar } from "context/notificationContext";
import { getErrorResponse } from "services/util";
import { useNavigate, useParams } from "react-router-dom";
import { useGetWidgetTypesData } from "hooks/widgets";
import { widget_types } from 'constants/widget-constant'
import { pointerWithin } from "@dnd-kit/core";
import { DragOverlay } from "@dnd-kit/core";
import { useGetTemplatesTypesData } from "hooks/templatesType"
import HeaderWidget from "components/widgets/HeaderWidget/HeaderWidget";
import EpisodeListingWidget from "components/widgets/EpisodeListing/EpisodeListingWidget";
import CategoryGrid from "components/widgets/CategoryGrid";
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import HomePageBanner from "components/widgets/HomePageBanner";
import ShowCard from "components/widgets/ShowCard";
import CollectionCard from "components/widgets/CollectionCard";
import VideoCarousel from 'components/widgets/VideoCarousel';
import ExpandedVideoCarousel from 'components/widgets/ExpandedVideoCarousel';
import MarketingBanner from 'components/widgets/MarketingBanner';
import SubscriptionWidget from 'components/widgets/SubscriptionWidget';
import ContinueWatching from 'components/widgets/ContinueWatching';
import WatchHistoryWidget from 'components/widgets/WatchHistoryWidget';
import VoiceWidgets from 'components/widgets/VoiceWidgets';
import { FAILURE_MESSAGE_CATEGORY_LABELS } from "containers/Protected/CustomCheckout/constants";
interface CanvasItem {
    uid: string;
    data: any;
    isEditing?: boolean;
    config?: any;
}

interface ValidWidgets {
    uid: string,
    value: boolean,
    widgetCode: string | null
}

export const AddUpdateTemplate = ({ mode }: { mode: 'create' | 'edit' }) => {
    const notification = useNotificationBar()
    const [canvasItems, setCanvasItems] = useState<CanvasItem[]>([]);
    const [templateName, setTemplateName] = useState("");
    const [isActive, setIsActive] = useState<boolean>(true);

    const { mutate: addTemplates } = useAddTemplates();
    const { mutate: updateTemplate } = useUpdateTemplateData();
    const { data: widgetData } = useGetWidgetTypesData()
    const widgetResponseData = widgetData?.data?.items ?? [];
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = mode === 'edit';
    const [isAnyDialogOpen, setIsAnyDialogOpen] = useState(false);
    const [validWidgets, setValidWidgets] = useState<ValidWidgets[]>([]);


    const { data: templateTypeRes, isLoading: isTemplateTypeLoading } = useGetTemplatesTypesData()
    const templateTypes = (templateTypeRes?.data?.results ?? []).map((t: any) => ({
        ...t,
        // 🔥 NORMALIZE BACKEND FIELDS
        template_code: t.template_code || t.template_type,   // FIX VALUE FIELD
        isDeleted: t.isDeleted ?? t.is_delete,               // FIX DELETE FLAG
    }))

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
            // 🚫 PREVENT DRAG WHEN ANY POPUP IS OPEN
            onActivation: (event) => {
                if (isAnyDialogOpen) {
                    return false;
                }
            },
        })
    );

    const { data: dataById } = useGetTemplateById(id);

    const editModeData = dataById?.data
    const templateTypeInitializedRef = useRef(false);

    const inferTemplateTypeFromWidgets = (widgets: any[]) => {
        if (!widgets?.length) return ''

        const widgetCodes = widgets.map(
            (w: any) =>
                w.data?.widget_code ||
                w.data?.code ||
                ''
        );

        if (widgetCodes.includes('HEADER_WIDGET') || widgetCodes.includes('HOME_BANNER'))
            return 'HOME'

        if (widgetCodes.includes('CATEGORY_GRID'))
            return 'CATEGORY'

        if (widgetCodes.includes('COLLECTION_CARD'))
            return 'COLLECTION'

        return 'SHOW'
    }

    const [activeWidget, setActiveWidget] = useState<any>(null);
    const [isDraggingOverCanvas, setIsDraggingOverCanvas] = useState(false);
    const [expandedWidgetId, setExpandedWidgetId] = useState<string | null>(null);

    useEffect(() => {
        if (!isEdit || !editModeData) return;

        // Set template name
        setTemplateName(
            editModeData.template_name ??
            editModeData.name ??
            ''
        );
        setIsActive(true);

        // ✅ Normalize widgets
        const rawWidgets =
            Array.isArray(editModeData.widgets)
                ? editModeData.widgets
                : Array.isArray(editModeData.data)
                    ? editModeData.data
                    : [];

        const normalizedWidgets: CanvasItem[] = rawWidgets.map((w: any) => {

            const widgetCode =
                w.widget_code ??
                w.data?.widget_code ??
                w.data?.code ??
                '';

            // const isEditableWidget =
            //     widgetCode === 'HEADER_WIDGET' ||
            //     widgetCode === 'EPISODE_LISTING';

            return {
                uid: w.uid || crypto.randomUUID(),
                data: {
                    id: w.widget_id ?? w.data?.id,
                    name: w.widget_name ?? w.data?.name,
                    widget_code: widgetCode,
                },
                isEditing: false,                 // collapsed by default
                config: w.widget_config || null,  // restore saved data
            };
        });


        setCanvasItems(normalizedWidgets);
        const prevEntry = normalizedWidgets.map((w: any) => ({ uid: w.uid, value: true, widgetCode: getWidgetCode(w) }))
        setValidWidgets(prev => [...prev, ...prevEntry]);

        // 🔒 CRITICAL: set template type ONLY ONCE
        if (!templateTypeInitializedRef.current) {
            const resolvedTemplateType =
                editModeData.template_type && editModeData.template_type.trim()
                    ? editModeData.template_type
                    : inferTemplateTypeFromWidgets(normalizedWidgets);

            setTemplateType(resolvedTemplateType);
            templateTypeInitializedRef.current = true;
        }

    }, [isEdit, editModeData]);

    useEffect(() => {
        templateTypeInitializedRef.current = false;
    }, [id]);

    useEffect(() => {
        console.log(
            'Widgets for library:',
            widgetResponseData.map(w => ({
                id: w.id,
                name: w.name,
                code: getWidgetCode(w),
            }))
        );
    }, [widgetResponseData]);

    const handleDragStart = (event: any) => {
        if (event.active.data?.current?.from === "library") {
            const widget = widgetResponseData.find(w => w.id === event.active.id);
            setActiveWidget(widget);
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveWidget(null);

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        if (active.data?.current?.from === 'library' &&
            over.data?.current?.type === 'canvas') {

            const widget = widgetResponseData.find(w => w.id === active.id);
            if (!widget) return;

            const widgetCode = getWidgetCode(widget);
            const isMarketingBannerWidget = widgetCode === widget_types.Marketing_Banner;

            // Check if marketing banner already exists in canvas (for wide banner only)
            // if (isMarketingBannerWidget) {
            //     // Check if this is a wide banner by looking at existing banner configurations
            //     const existingWideBanner = canvasItems.find((item: any) => {
            //         const existingWidgetCode = getWidgetCode(item.data);
            //         if (existingWidgetCode === widget_types.Marketing_Banner) {
            //             const bannerType = item.config?.bannerType || 'wide'; // Default to wide if not specified
            //             return bannerType === 'wide';
            //         }
            //         return false;
            //     });

            //     // For new widgets, assume wide type until configured
            //     const isNewWideBanner = true; // New marketing banners default to wide type

            //     if (existingWideBanner && isNewWideBanner) {
            //         notification.push({
            //             message: 'Only one Wide Banner widget is allowed per template',
            //             type: 'error',
            //         });
            //         return;
            //     }
            // }

            const isHeaderWidget = widgetCode === 'HEADER_WIDGET';
            const isEpisodeWidget = widgetCode === 'EPISODE_LISTING';

            const skipValidationCodes = [
                'BESPOKE_EPISODES_ROWS',
                'AUTO_GENERATED_SHOW_ROWS',
                'AUTO_GENERATED_EPISODES_ROWS',
                'AUTO_GENERATED_COLLECTION_ROWS'
            ];

            const newItem = {
                uid: nanoid(),
                data: widget,
                isEditing: false,
                config: null,
            };

            setCanvasItems((prev: any) => [...prev, newItem]);
            const isSkipWidget = skipValidationCodes.includes(widgetCode);
            setValidWidgets(prev => [...prev,
            {
                uid: newItem.uid,
                // value: false,
                value: isSkipWidget ? true : false,
                widgetCode: getWidgetCode(widget)
            }]);
        }
        setCanvasItems(prev => {
            const oldIndex = prev.findIndex(
                (item: any) => item.uid === activeId
            );
            const newIndex = prev.findIndex(
                (item: any) => item.uid === overId
            );

            if (oldIndex === -1 || newIndex === -1) return prev;

            return arrayMove(prev, oldIndex, newIndex);
        });
        if (!over || over.id !== "canvas") return;

    };

    const handleSaveTemplate = () => {

        const isWidgetNotFilled = validWidgets.find(w => w.value === false)
        if (isWidgetNotFilled) {
            notification.push({
                title: 'Template not saved',
                message: `The ${isWidgetNotFilled.widgetCode} widget is incomplete. Please fill in all required fields before saving.`,
                type: 'error'
            });
            return
        }



        const templatePayload = {
            name: templateName,
            template_type: templateType,
            status: isActive ? 1 : 0,

            data: canvasItems.map((item: any, index: number) => {
                const widget = item.data;
                const widgetCode = getWidgetCode(widget);

                let widgetConfig = item.config || {};
                return {
                    widget_id: widget.id,
                    widget_name: item.config?.name || widget.name,
                    widget_code: widgetCode,
                    widget_order: index + 1,

                    // ✅ SAVE HEADER CONFIG HERE
                    // widget_config: widgetConfig
                    widget_config: widgetConfig && Object.keys(widgetConfig).length > 0
                        ? {
                            ...widgetConfig,
                            widget_type: widget.id,
                        }
                        : {
                            widget_type: widget.id,
                            data: {}
                        },
                };
            })
        }
        if (id) {
            updateTemplate(
                {
                    id: id,
                    data: templatePayload,
                },
                {
                    onError(error: any) {
                        notification.push(getErrorResponse(error));
                    },
                    onSuccess() {
                        notification.push({
                            title: 'Success!',
                            message: 'Template has been updated successfully!',
                            type: 'success',
                        });
                        navigate(-1);
                    },
                }
            );
            return;
        }
        addTemplates({
            data: templatePayload
        }, {
            onError(error: any) {
                notification.push(getErrorResponse(error))
            },
            onSuccess(data, variables, context) {
                notification.push({
                    title: 'Success !',
                    message: 'Template has been created successfully !',
                    type: 'success'
                })
                setCanvasItems([])
                navigate('/template/template-management');
            },
        })
    };

    const removeItem = (uid: string) => {
        setCanvasItems((prev: any) => prev.filter((x: any) => x.uid !== uid));
        setValidWidgets(prev => prev.filter((x: any) => x.uid !== uid));
    };

    const [templateType, setTemplateType] = useState('')
    // const TEMPLATE_WIDGET_MAP: Record<string, string[]> = {
    //     HOME: [], // show ALL widgets
    //     EXPLORE: [], // show ALL widgets

    //     CATEGORY: [
    //         'HEADER_WIDGET',
    //         'CATEGORY_GRID',
    //         'COLLECTION_CARD',
    //         'EPISODE_LISTING',
    //     ],

    // const TEMPLATE_WIDGET_MAP: Record<string, string[]> = {
    //     HOME: [], // show ALL widgets
    //     Explore: [], // show ALL widgets

    //     COLLECTION: [
    //         'HEADER_WIDGET',
    //         'HOMEPAGE_BANNER_CAROUSEL',
    //         'COLLECTION_CARD',
    //         'EPISODE_LISTING',
    //     ],

    //     SHOW: [
    //         'HEADER_WIDGET',
    //         'HOMEPAGE_BANNER_CAROUSEL',
    //         'EPISODE_LISTING',
    //     ],
    // };

    const getWidgetCode = (w: any) => {
        return (
            w?.data?.widget_code ||
            w?.widget_code ||
            w?.code ||
            ''
        );
    };

    const filteredWidgets = useMemo(() => {
        if (!templateType || !templateTypes?.length || !widgetResponseData?.length) {
            return [];
        }

        const widgetCodes =
            templateTypes.find(
                (template) => template.template_code === templateType
            )?.widgetsCodes?.widgets ?? [];

        return widgetResponseData.filter((widget: any) =>
            widgetCodes.includes(widget.code)
        );
    }, [templateType, templateTypes, widgetResponseData]);

    return (
        <PageInnerLayout title={id ? "Edit Template" : "Create Template"}>
            <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                    <div className="flex items-center justify-between gap-4">

                        <div className="flex items-center gap-4 flex-1 justify-start grid grid-cols-12 gap-6">
                            <div className="col-span-3 pr-4 pb-4 pt-4 border-r border-slate-300 dark:border-slate-700">

                                {/* Template Type */}
                                <select
                                    value={templateType}
                                    onChange={(e) => {
                                        setTemplateType(e.target.value)
                                        setCanvasItems([])
                                    }}
                                    disabled={isTemplateTypeLoading || isEdit}
                                    className="px-3 py-2 border rounded-md text-sm
                                                bg-gray-100 dark:bg-slate-700
                                                border-gray-300 dark:border-gray-600
                                                w-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
                                >
                                    <option value="">Select Template Type</option>

                                    {templateTypes
                                        .filter(t => t.status === 1 && t.isDeleted === 0)
                                        .map((type) => (
                                            <option key={type.id} value={type.template_code}>
                                                {type.name}
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <div className="col-span-9 p-3 flex items-center gap-4 justify-end">
                                {/* Template Name */}
                                <input
                                    type="text"
                                    placeholder="Enter template name"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    className="w-full max-w-sm px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm
                 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 dark:bg-slate-700 mr-auto dark:text-white"
                                />

                                {/* Active / Inactive Toggle */}
                                <div className="flex gap-0 items-center">
                                    <button
                                        onClick={() => setIsActive(!isActive)}
                                        className={`w-10 h-5 rounded-full p-1 transition ${isActive ? "bg-blue-600" : "bg-gray-300"
                                            }`}
                                    >
                                        <div
                                            className={`h-3 w-3 rounded-full bg-white transform transition ${isActive ? "translate-x-5" : "translate-x-0"
                                                }`}
                                        />
                                    </button>
                                    {/* Tooltip */}
                                    <div className="relative group mr-auto ml-2 mt-0">
                                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                        <div className="
                                                                        absolute left-1/2 -translate-x-1/2 top-full mt-1
                                                                        hidden group-hover:block
                                                                        whitespace-nowrap
                                                                        bg-black text-white text-xs
                                                                        px-2 py-1 rounded
                                                                        z-50
                                                                    ">
                                            Active / Inactive Toggle
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    color="blue"
                                    type="submit"
                                    onClick={handleSaveTemplate}
                                    disabled={!templateName.trim() || canvasItems.length === 0}
                                >
                                    {id ? 'Update Template' : 'Save Template'}
                                </Button>
                            </div>




                        </div>


                    </div>
                </div>
            </div>
            <div className="grid grid-cols-12 gap-6 h-[65vh]">
                <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={(event) => {
                        if (event.active.data?.current?.from === "library") {
                            setIsDraggingOverCanvas(false);
                        }
                    }}
                    onDragOver={(event) => {
                        setIsDraggingOverCanvas(event.over?.id === "canvas");
                    }}
                    onDragEnd={(event) => {
                        setIsDraggingOverCanvas(false);
                        handleDragEnd(event);
                    }}
                >

                    <DragOverlay>
                        {activeWidget ? (
                            <div className="w-64 p-3 bg-white shadow-lg rounded-lg">
                                {activeWidget.name}
                            </div>
                        ) : null}
                    </DragOverlay>
                    {/** LEFT COLUMN — WIDGET LIBRARY */}
                    <div className="col-span-3 border-r border-slate-300 dark:border-slate-700 pr-4">
                        <div className="grid grid-cols-1 gap-2 pb-3">
                            {filteredWidgets.map((widget) => {
                                const isExpanded = expandedWidgetId === widget.id;
                                const widgetCode = getWidgetCode(widget);
                                const isMarketingBannerWidget = widgetCode === widget_types.Marketing_Banner;

                                // Check if wide banner already exists in canvas
                                // const existingWideBanner = canvasItems.find((item: any) => {
                                //     const existingWidgetCode = getWidgetCode(item.data);
                                //     if (existingWidgetCode === widget_types.Marketing_Banner) {
                                //         const bannerType = item.config?.bannerType || 'wide'; // Default to wide if not specified
                                //         return bannerType === 'wide';
                                //     }
                                //     return false;
                                // });

                                //const isMarketingBannerDisabled = Boolean(isMarketingBannerWidget && existingWideBanner);

                                return (
                                    <DraggableWidget
                                        key={widget.id}
                                        id={widget.id}
                                        disabled={false}
                                    >
                                        <WidgetLibraryCard
                                            widget={widget}
                                            isExpanded={isExpanded}
                                            onToggle={() => setExpandedWidgetId(isExpanded ? null : widget.id)}
                                            disabled={false}
                                            disabledMessage="Only one Wide Banner widget is allowed per template"
                                        />
                                    </DraggableWidget>
                                );
                            })}
                            {/* No widgets available message */}
                            {!templateType && !isEdit && (
                                <div className="flex h-full w-full items-center justify-center">
                                    <div
                                        className="flex items-center gap-3 w-full rounded-lg border border-dashed border-slate-300 px-5 py-4
           text-slate-600
           dark:border-slate-600 dark:text-slate-400"
                                    >
                                        <span className="text-sm font-medium text-red-500">
                                            Select Template Type to load widgets
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/** RIGHT COLUMN – TEMPLATE BUILDER */}
                    <div className="col-span-9 p-3 overflow-y-auto">

                        {/* <SortableContext items={canvasItems} strategy={verticalListSortingStrategy}> */}
                        <SortableContext items={canvasItems.map((item: any) => item.uid)} strategy={verticalListSortingStrategy}>
                            <div className="">
                                <CanvasDropArea
                                    isDraggingOverCanvas={isDraggingOverCanvas}
                                    isEmpty={canvasItems.length === 0}
                                    hasItems={canvasItems.length > 0}
                                    itemCount={canvasItems.length}
                                >

                                    <div className="grid grid-cols-1">
                                        {canvasItems.map((item: any) => {
                                            const widget = widgetResponseData.find((w) => w.id === item.data.id);
                                            const widgetCode = getWidgetCode(widget);
                                            const isHeaderWidget = widgetCode === widget_types.Header_Widget;
                                            const isEpisodeWidget = widgetCode === widget_types.Episode_list;
                                            const isCategoryGridWidget = widgetCode === widget_types.Category_Grid;
                                            const isHomeBannerWidget = widgetCode === widget_types.HomePage_Banner_Carousel;
                                            const isShowCardWidget = widgetCode === widget_types.Show_Card;
                                            const isCollectionCardWidget = widgetCode === widget_types.Collection_card;
                                            const isVideoCarouselWidget = widgetCode === widget_types.Video_Carousel;
                                            const isExpandedVideoCarouselWidget = widgetCode === widget_types.Expanded_Video_Carousel;
                                            const isMarketingBannerWidget = widgetCode === widget_types.Marketing_Banner;
                                            const isSubscriptionWidget = widgetCode === widget_types.Subscription_Widget;
                                            const isContinueWatchingWidget = widgetCode === widget_types.Continue_Watching;
                                            const isWatchHistoryWidget = widgetCode === widget_types.Watch_History;
                                            const isVoiceWidget = widgetCode === widget_types.Voice_Widgets;

                                            const isEditableWidget =
                                                isHeaderWidget || isEpisodeWidget || isHomeBannerWidget || isCategoryGridWidget || isShowCardWidget || isCollectionCardWidget || isVideoCarouselWidget || isExpandedVideoCarouselWidget || isMarketingBannerWidget || isSubscriptionWidget || isContinueWatchingWidget || isWatchHistoryWidget || isVoiceWidget;

                                            return (
                                                <SortableWidgets key={item.uid} id={item.uid} disabled={isAnyDialogOpen}>
                                                    <div className="relative canvas-item flex justify-center w-full p-1">

                                                        {/* delete button */}
                                                        <button
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                            onClick={() => removeItem(item.uid)}
                                                            className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow hover:bg-red-50"
                                                        >
                                                            <TrashIcon className="w-4 h-4 text-red-600" />
                                                        </button>

                                                        {/* EDITABLE WIDGETS: HEADER & EPISODE */}
                                                        {isEditableWidget && (
                                                            <div className="w-full border rounded-lg bg-slate-100 dark:bg-slate-700">

                                                                {/* HEADER BAR */}
                                                                <div className="flex items-center justify-between px-4 py-3" onClick={() =>
                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                        prev.map((ci) =>
                                                                            ci.uid === item.uid
                                                                                ? { ...ci, isEditing: !ci.isEditing }
                                                                                : { ...ci, isEditing: false }
                                                                        )
                                                                    )
                                                                }>
                                                                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                                                        {widget?.name}
                                                                    </span>

                                                                    {/* Tooltip */}
                                                                    <div className="relative group mr-auto ml-2 mt-0">
                                                                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                                                        <div className="
                                                                           absolute left-1/2 -translate-x-1/2 bottom-full mb-1
                                                                            hidden group-hover:block
                                                                            whitespace-nowrap
                                                                            bg-black text-white text-xs
                                                                            px-2 py-1 rounded
                                                                            z-50
                                                                    ">
                                                                            Editable Widget
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        // onClick={() =>
                                                                        //     setCanvasItems((prev: CanvasItem[]) =>
                                                                        //         prev.map((ci) =>
                                                                        //             ci.uid === item.uid
                                                                        //                 ? { ...ci, isEditing: !ci.isEditing }
                                                                        //                 : ci
                                                                        //         )
                                                                        //     )
                                                                        // }
                                                                        className="text-slate-600 hover:text-blue-600"
                                                                    >
                                                                        {item.isEditing ? '▾' : '▸'}
                                                                    </button>
                                                                </div>

                                                                {/* EXPAND CONTENT */}
                                                                {item.isEditing && (
                                                                    <div className="px-4 pb-4 pt-4 rounded-lg border-t border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800 w-full">

                                                                        {/* HEADER WIDGET */}
                                                                        {isHeaderWidget && (
                                                                            <HeaderWidget
                                                                                widget={{
                                                                                    value: widget.widget_type,
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? { ...ci, config: payload, isEditing: false }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                    setIsAnyDialogOpen(true);
                                                                                }}
                                                                                onCancel={() => {

                                                                                    setIsAnyDialogOpen(false);
                                                                                }}
                                                                                initialData={item.config}
                                                                            />

                                                                        )}

                                                                        {/* EPISODE LISTING WIDGET */}
                                                                        {isEpisodeWidget && (
                                                                            <EpisodeListingWidget
                                                                                widget={{
                                                                                    value: widget.widget_type,
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? { ...ci, config: payload, isEditing: false }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                    setIsAnyDialogOpen(true);
                                                                                }}
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config}
                                                                            />
                                                                        )}
                                                                        {/* CATEGORY GRID */}
                                                                        {isCategoryGridWidget && (
                                                                            <CategoryGrid
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: widget.id,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? { ...ci, config: payload, isEditing: false }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                    setIsAnyDialogOpen(true);
                                                                                }}
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config}
                                                                            />
                                                                        )}
                                                                        {/* HOMEPAGE BANNER CAROUSEL */}
                                                                        {isHomeBannerWidget && (
                                                                            <HomePageBanner
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: Number(widget.id),
                                                                                }}
                                                                                items={item.config?.data?.widget_items || []}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                    setIsAnyDialogOpen(true);
                                                                                }}
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config}
                                                                            />
                                                                        )}

                                                                        {isShowCardWidget && (
                                                                            <ShowCard
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: Number(widget.id),
                                                                                }}
                                                                                items={item.config?.data?.widget_items || []}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                    setIsAnyDialogOpen(true);
                                                                                }}
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config} template_type={templateType} />
                                                                        )}

                                                                        {isCollectionCardWidget && (
                                                                            <CollectionCard
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: Number(widget.id),
                                                                                }}
                                                                                items={item.config?.data?.widget_items || []}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    setIsAnyDialogOpen(true);
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                }}
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config} />
                                                                        )}
                                                                        {isVideoCarouselWidget && (
                                                                            <VideoCarousel
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: Number(widget.id),
                                                                                }}
                                                                                items={item.config?.data?.widget_items || []}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                    setIsAnyDialogOpen(true);
                                                                                }}
                                                                                onCancel={() => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    setIsAnyDialogOpen(false);
                                                                                }}
                                                                                initialData={item.config} template_type={templateType} />
                                                                        )}
                                                                        {isExpandedVideoCarouselWidget && (
                                                                            <ExpandedVideoCarousel
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: Number(widget.id),
                                                                                }}
                                                                                items={item.config?.data?.widget_items || []}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                    setIsAnyDialogOpen(true);
                                                                                }}
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config} template_type={templateType} />
                                                                        )}
                                                                        {isMarketingBannerWidget && (
                                                                            <MarketingBanner
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: widget.id,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    )
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload.widget_code }]);
                                                                                }
                                                                                }
                                                                                initialData={item.config} />
                                                                        )}
                                                                        {isSubscriptionWidget && (
                                                                            <SubscriptionWidget
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: widget.id,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    )

                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload?.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload?.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload?.widget_code ?? widgetCode }]);
                                                                                }

                                                                                }
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config} />
                                                                        )}
                                                                        {isContinueWatchingWidget && (
                                                                            <ContinueWatching
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: widget.id,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    )
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload?.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload?.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload?.widget_code ?? widgetCode }]);
                                                                                }
                                                                                }
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config} />
                                                                        )}
                                                                        {isVoiceWidget && (
                                                                            <VoiceWidgets
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: widget.id,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload?.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload?.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload?.widget_code ?? widgetCode }]);
                                                                                }}
                                                                                onCancel={() => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    setIsAnyDialogOpen(false);
                                                                                }}
                                                                                initialData={item.config}
                                                                            />
                                                                        )}
                                                                        {isWatchHistoryWidget && (
                                                                            <WatchHistoryWidget
                                                                                widget={{
                                                                                    label: widget.name,
                                                                                    code: widgetCode,
                                                                                    value: widget.id,
                                                                                }}
                                                                                onSave={(payload) => {
                                                                                    setCanvasItems((prev: CanvasItem[]) =>
                                                                                        prev.map((ci) =>
                                                                                            ci.uid === item.uid
                                                                                                ? {
                                                                                                    ...ci,
                                                                                                    config: payload,
                                                                                                    isEditing: false,
                                                                                                }
                                                                                                : ci
                                                                                        )
                                                                                    );
                                                                                    const validW = validWidgets.find((w: any) => w.widgetCode === payload?.widget_code && w.uid === item.id);
                                                                                    const prevValidWidgets = validWidgets.filter((w: any) => w.widgetCode !== payload?.widget_code);
                                                                                    setValidWidgets([...prevValidWidgets, { uid: validW?.uid ?? item.id, value: true, widgetCode: payload?.widget_code ?? widgetCode }]);
                                                                                }}
                                                                                onCancel={() => setIsAnyDialogOpen(false)}
                                                                                initialData={item.config}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* NON-EDITABLE WIDGETS (ALL OTHERS) */}
                                                        {!isEditableWidget && (
                                                            <div className="w-full rounded-lg border px-4 py-3 bg-slate-100 dark:bg-slate-700">
                                                                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                                                    {item.data?.name || widget?.name || '—'}
                                                                </span>
                                                            </div>
                                                        )}

                                                    </div>
                                                </SortableWidgets>
                                            );
                                        })}

                                    </div>
                                </CanvasDropArea>
                            </div>
                        </SortableContext>
                    </div>
                </DndContext>
            </div >
        </PageInnerLayout>
    );
};
